import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Add, Remove } from '@mui/icons-material';
import './SpecialStatWidget.css';
import * as stats from '../../core/st';
import SliderButton from '../utils/SliderButton';

const TXT_LEVEL = { fr: 'Niveau', en: 'Level', de: 'Stufe', es: 'Nivel', pt: 'Nível' };
const TXT_LVL = { fr: 'Niv.', en: 'Lvl.', de: 'St.', es: 'Niv.', pt: 'Nív.' };

const SpecialStatWidget = ({ lvl, stat, onDesiredValueChange, onLevelChange, lang = 'fr', isGenerating = false }) => {
    // Always call hooks first, even if stat is undefined

    const {
        id,
        alias_fr,
        alias_en,
        icon,
        current = 0,
        desired = 0,
        minRange = 0,
        maxRange = 0,
    } = stat;

    const alias_value = lang === "fr" ? alias_fr : alias_en;

    const isLvl = id === stats.LVL_ID;
    const isAP = id === stats.AP_ID;
    const delta = useMemo(() => (Number.isFinite(current) && Number.isFinite(desired)) ? current - desired : 0, [current, desired]);

    const effectiveMinRange = useMemo(() => {
        if (isLvl) return 1;
        if (isAP) return lvl >= 100 ? 7 : 6; // Special case for PA
        return minRange;
    }, [lvl, minRange, isLvl, isAP]);

    const effectiveMaxRange = isLvl ? 200 : maxRange;

    // Defensive: ensure value is always a number
    const initialValue = isLvl ? lvl : Math.max(Number.isFinite(desired) ? desired : effectiveMinRange, effectiveMinRange);
    const [value, setValue] = useState(initialValue);

    useEffect(() => {
        if (!isLvl) {
            setValue(Math.max(Number.isFinite(desired) ? desired : effectiveMinRange, effectiveMinRange));
        }
    }, [desired, effectiveMinRange, isLvl]);

    // Keep the level spinner in sync when the underlying build's level changes
    // (switching builds, applying an AI/generated build, remote save round-trips).
    // Without this, `value` stays stale and a +/- nudge writes the wrong level back.
    useEffect(() => {
        if (isLvl && Number.isFinite(lvl)) {
            setValue(lvl);
        }
    }, [lvl, isLvl]);

    const handleIncrement = useCallback(() => {
        if (value < effectiveMaxRange) {
            const newValue = value + 1;
            setValue(newValue);
            if (isLvl) {
                onLevelChange?.(newValue);
            } else {
                onDesiredValueChange?.(id, newValue);
            }
        }
    }, [value, effectiveMaxRange, id, onDesiredValueChange, onLevelChange, isLvl]);

    const handleDecrement = useCallback(() => {
        if (value > effectiveMinRange) {
            const newValue = value - 1;
            setValue(newValue);
            if (isLvl) {
                onLevelChange?.(newValue);
            } else {
                onDesiredValueChange?.(id, newValue);
            }
        }
    }, [value, effectiveMinRange, id, onDesiredValueChange, onLevelChange, isLvl]);

    const handleInputChange = (e) => {
        const input = parseInt(e.target.value, 10);
        const newValue = Math.max(
            effectiveMinRange,
            Math.min(effectiveMaxRange, Number.isFinite(input) ? input : effectiveMinRange)
        );
        setValue(newValue);
        if (isLvl) {
            onLevelChange?.(newValue);
        } else {
            onDesiredValueChange?.(id, newValue);
        }
    };

    // Dragging only moves the local value; the build (and everything derived from
    // it) updates once, on release — updating per tick made the slider lag.
    const handleSliderChange = useCallback(
        (newValue) => {
            const safe = Number.isFinite(newValue) ? newValue : effectiveMinRange;
            setValue(Math.max(effectiveMinRange, Math.min(effectiveMaxRange, safe)));
        },
        [effectiveMinRange, effectiveMaxRange]
    );

    const handleSliderCommit = useCallback(
        (newValue) => {
            const safe = Number.isFinite(newValue) ? newValue : effectiveMinRange;
            onLevelChange?.(Math.max(effectiveMinRange, Math.min(effectiveMaxRange, safe)));
        },
        [effectiveMinRange, effectiveMaxRange, onLevelChange]
    );

    const incrementRef = useRef();
    const incrementTimeoutRef = useRef();
    const decrementRef = useRef();
    const decrementTimeoutRef = useRef();

    const handleIncrementMouseDown = () => {
        handleIncrement();
        incrementTimeoutRef.current = setTimeout(() => {
            incrementRef.current = setInterval(handleIncrement, 60);
        }, 300);
    };
    const handleIncrementMouseUp = () => {
        clearTimeout(incrementTimeoutRef.current);
        clearInterval(incrementRef.current);
    };
    const handleIncrementMouseLeave = handleIncrementMouseUp;

    const handleDecrementMouseDown = () => {
        handleDecrement();
        decrementTimeoutRef.current = setTimeout(() => {
            decrementRef.current = setInterval(handleDecrement, 60);
        }, 300);
    };
    const handleDecrementMouseUp = () => {
        clearTimeout(decrementTimeoutRef.current);
        clearInterval(decrementRef.current);
    };
    const handleDecrementMouseLeave = handleDecrementMouseUp;

    // Always render the UI, even if stat is missing (show empty/default values)
    return (
        <>
            {isLvl ? (
                <SliderButton
                    value={value}
                    onChange={handleSliderChange}
                    onCommit={handleSliderCommit}
                    min={effectiveMinRange}
                    max={effectiveMaxRange}
                    step={1}
                    disabled={isGenerating}
                    label={TXT_LEVEL[lang] || TXT_LEVEL.en}
                    className="lvl-slider-button"
                >
                    <div className="special-stat-params">
                        <div className="spinner">
                            <span className="lvl-label-text">{TXT_LVL[lang] || TXT_LVL.en}</span>
                            <input
                                type="number"
                                value={Number.isFinite(value) ? value : ''}
                                onChange={handleInputChange}
                                className="lvl spinner-input centered"
                                disabled={isGenerating}
                            />
                            <div className="spinner-buttons">
                                <button
                                    type="button"
                                    onMouseDown={handleIncrementMouseDown}
                                    onMouseUp={handleIncrementMouseUp}
                                    onMouseLeave={handleIncrementMouseLeave}
                                    className="spinner-button"
                                    disabled={isGenerating}
                                >
                                    <Add fontSize="small" />
                                </button>
                                <button
                                    type="button"
                                    onMouseDown={handleDecrementMouseDown}
                                    onMouseUp={handleDecrementMouseUp}
                                    onMouseLeave={handleDecrementMouseLeave}
                                    className="spinner-button"
                                    disabled={isGenerating}
                                >
                                    <Remove fontSize="small" />
                                </button>
                            </div>
                        </div>
                    </div>
                </SliderButton>
            ) : (
                <>
                    <div className="special-stat-params">
                        {icon && <img src={icon} className="stat-icon" style={{ width: 16, height: 16 }} alt={alias_value || 'Stat'} />}
                        <div className="spinner">
                            <input
                                type="number"
                                value={Number.isFinite(value) ? value : ''}
                                onChange={handleInputChange}
                                className="spinner-input centered"
                                disabled={isGenerating}
                            />
                            <div className="spinner-buttons">
                                <button
                                    type="button"
                                    onMouseDown={handleIncrementMouseDown}
                                    onMouseUp={handleIncrementMouseUp}
                                    onMouseLeave={handleIncrementMouseLeave}
                                    className="spinner-button"
                                    disabled={isGenerating}
                                >
                                    <Add fontSize="small" />
                                </button>
                                <button
                                    type="button"
                                    onMouseDown={handleDecrementMouseDown}
                                    onMouseUp={handleDecrementMouseUp}
                                    onMouseLeave={handleDecrementMouseLeave}
                                    className="spinner-button"
                                    disabled={isGenerating}
                                >
                                    <Remove fontSize="small" />
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="special-stat-info">
                        <div className={`current-value ${delta >= 0 ? 'green' : 'yellow'}`}>{Number.isFinite(current) ? current : 0}</div>
                        <div className="stat-label">{alias_value}</div>
                        {/* {!isLevelStat && <span className="special-stat-slash">•</span>} */}
                    </div>
                </>
            )}
        </>
    );
};

export default SpecialStatWidget;
